/**
 * ShareCard — the visual card that gets exported to PNG when the user taps
 * Share. Brief Section 7.4: 400×200 canvas, wordmark top-left, emoji string
 * centred, date + stadium right, streak/score bottom-left, URL bottom-right.
 *
 * The component renders the card to its visible <canvas> on every prop
 * change and exposes a `toBlob()` imperative handle so the parent can grab
 * a PNG to hand to navigator.share() or the clipboard fallback.
 *
 * Drawing is DPR-aware (capped at 2 to keep the share PNG under ~80 KB).
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const CARD_W = 400;
const CARD_H = 200;

export type ShareCardAccent = "emerald" | "amber" | "rose" | "slate";

export interface ShareCardData {
  /** Emoji string e.g. "⚽↗🥅✨". */
  shareText: string;
  /** Outcome headline e.g. "GOAL!". Drawn in the accent colour. */
  headline: string;
  /** Score for GOAL outcomes; null otherwise. */
  score: number | null;
  /** Current streak; null/0 hides the line. */
  streak: number | null;
  /** ISO date YYYY-MM-DD. */
  date: string;
  /** Stadium flavour text; null hides the line. */
  stadium: string | null;
  /** Drives the headline + accent line colour. */
  accent: ShareCardAccent;
}

export interface ShareCardHandle {
  toBlob: () => Promise<Blob | null>;
}

const ACCENT_HEX: Record<ShareCardAccent, string> = {
  emerald: "#6ee7b7",
  amber: "#fcd34d",
  rose: "#fda4af",
  slate: "#cbd5e1",
};

function drawCard(canvas: HTMLCanvasElement, d: ShareCardData): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.floor(CARD_W * dpr);
  canvas.height = Math.floor(CARD_H * dpr);

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, CARD_W, CARD_H);

  // Background — soft vertical gradient evoking the pitch palette.
  const grad = ctx.createLinearGradient(0, 0, 0, CARD_H);
  grad.addColorStop(0, "#0b3a2e");
  grad.addColorStop(1, "#04140f");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Subtle accent stripe at the bottom.
  ctx.fillStyle = ACCENT_HEX[d.accent];
  ctx.globalAlpha = 0.6;
  ctx.fillRect(0, CARD_H - 3, CARD_W, 3);
  ctx.globalAlpha = 1;

  const fontStack = "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

  // Wordmark — top-left.
  ctx.fillStyle = "#ffffff";
  ctx.font = `700 22px ${fontStack}`;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillText("CURVLE", 20, 18);

  // Date + stadium — top-right, faint.
  ctx.font = `500 12px ${fontStack}`;
  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(255,255,255,0.65)";
  ctx.fillText(d.date, CARD_W - 20, 20);
  if (d.stadium) {
    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.fillText(d.stadium, CARD_W - 20, 36);
  }

  // Big emoji string — centred. Use a slightly larger font and let the
  // browser fall back to the system colour-emoji font.
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `600 38px ${fontStack}`;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(d.shareText, CARD_W / 2, CARD_H / 2 - 4);

  // Headline below emoji in the accent colour.
  ctx.font = `700 13px ${fontStack}`;
  ctx.fillStyle = ACCENT_HEX[d.accent];
  ctx.fillText(d.headline.toUpperCase(), CARD_W / 2, CARD_H / 2 + 30);

  // Bottom row.
  const bottomY = CARD_H - 22;
  ctx.font = `600 12px ${fontStack}`;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";

  // Score takes priority for GOAL; otherwise show streak if present.
  if (d.score != null && d.score > 0) {
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fillText(`Score ${d.score}`, 20, bottomY);
    if (d.streak != null && d.streak > 0) {
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.font = `500 11px ${fontStack}`;
      ctx.fillText(`🔥 ${d.streak}`, 100, bottomY + 1);
    }
  } else if (d.streak != null && d.streak > 0) {
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.fillText(`🔥 Streak ${d.streak}`, 20, bottomY);
  }

  // URL — bottom-right.
  ctx.font = `500 11px ${fontStack}`;
  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.fillText("www.curvle.world", CARD_W - 20, bottomY + 1);
}

export const ShareCard = forwardRef<ShareCardHandle, ShareCardData>(function ShareCard(data, ref) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawCard(canvas, data);
  }, [data.shareText, data.headline, data.score, data.streak, data.date, data.stadium, data.accent]);

  useImperativeHandle(
    ref,
    () => ({
      toBlob: () =>
        new Promise<Blob | null>((resolve) => {
          const canvas = canvasRef.current;
          if (!canvas) {
            resolve(null);
            return;
          }
          canvas.toBlob((b) => resolve(b), "image/png", 0.95);
        }),
    }),
    [],
  );

  return (
    <canvas
      ref={canvasRef}
      className="w-full rounded-xl shadow-lg shadow-black/30"
      style={{ aspectRatio: `${CARD_W} / ${CARD_H}` }}
      aria-label="Share card preview"
    />
  );
});
